import React, { useState, useEffect, useRef } from 'react';
import { Modal, FormLayout, TextField, Select, Banner } from '@shopify/polaris';
import { ordersService } from '../../services/ordersService';
import { getPaymentBalance, ADDITIONAL_PAYMENT_METHODS } from '../../utils/orderEditRules';

const METHOD_OPTIONS = ADDITIONAL_PAYMENT_METHODS;

const makeRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `payment-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const validateAmount = (input, amountDue) => {
  const text = String(input ?? '').trim();
  if (!text) return 'Enter an amount';
  const value = Number(text);
  if (!Number.isFinite(value) || !/^\d+(\.\d+)?$/.test(text)) return 'Enter a valid amount';
  if (value <= 0) return 'Amount must be greater than 0';
  if (/\.\d{3,}$/.test(text)) return 'At most 2 decimal places';
  if (value > amountDue) return `Amount cannot exceed the amount due (${amountDue.toFixed(2)})`;
  return null;
};

const RecordPaymentModal = ({ isOpen, order, onClose, onRecorded }) => {
  const orderId = order?.order_id;
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(METHOD_OPTIONS[0].value);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(null);
  const submittingRef = useRef(false);
  const orderRef = useRef(order);
  orderRef.current = order;

  useEffect(() => {
    if (!isOpen) return;
    const due = getPaymentBalance(orderRef.current).amount_due;
    setAmount(String(due));
    setMethod(METHOD_OPTIONS[0].value);
    setReference('');
    setNotes('');
    setError('');
    setSubmitting(false);
    submittingRef.current = false;
    requestIdRef.current = makeRequestId();
  }, [isOpen, orderId]);

  const amountDue = getPaymentBalance(order).amount_due;
  const amountError = isOpen ? validateAmount(amount, amountDue) : null;
  const methodError = method ? null : 'Select a payment method';

  const handleClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (submittingRef.current || amountError || methodError) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const result = await ordersService.recordAdditionalPayment(orderId, {
        amount: Number(amount),
        method,
        reference: reference.trim(),
        notes: notes.trim(),
        payment_request_id: requestIdRef.current
      });
      submittingRef.current = false;
      setSubmitting(false);
      onRecorded(result);
    } catch (err) {
      submittingRef.current = false;
      setSubmitting(false);
      setError(err?.message || 'Failed to record payment');
    }
  };

  return (
    <Modal
      open={!!isOpen}
      onClose={handleClose}
      title="Record payment"
      primaryAction={{
        content: 'Record payment',
        onAction: handleSubmit,
        loading: submitting,
        disabled: !!amountError || !!methodError || submitting
      }}
      secondaryActions={[{ content: 'Cancel', onAction: handleClose, disabled: submitting }]}
    >
      <Modal.Section>
        <FormLayout>
          {error && <Banner tone="critical">{error}</Banner>}
          <TextField
            label="Amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            onChange={setAmount}
            error={amountError || undefined}
            helpText={`Amount due: ${amountDue.toFixed(2)}`}
            disabled={submitting}
          />
          <Select
            label="Payment method"
            options={METHOD_OPTIONS}
            value={method}
            onChange={setMethod}
            error={methodError || undefined}
            disabled={submitting}
          />
          <TextField
            label="Reference"
            autoComplete="off"
            value={reference}
            onChange={setReference}
            disabled={submitting}
          />
          <TextField
            label="Notes"
            multiline={3}
            autoComplete="off"
            value={notes}
            onChange={setNotes}
            disabled={submitting}
          />
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
};

export default RecordPaymentModal;
